import { useEffect, useMemo, useState } from 'react';
import adminApi from '@/lib/adminApi';

const emptyProduct = {
  artist_id: '',
  title: '',
  description: '',
  category: 'Original Painting',
  subcategory: '',
  product_type: 'physical',
  price_inr: '',
  discount_percent: 0,
  inventory_count: 1,
  image_urls_text: '',
  digital_asset_url: '',
  shipping_from: '',
  shipping_from_other: '',
  dimensions: '',
  dimension_value: '',
  dimension_unit: 'inches',
  medium: '',
  status: 'draft',
  commission_percent: 0,
  admin_note: '',
};

const productCategories = [
  'Original Painting',
  'Sculpture',
  'Handmade Craft',
  'Photography',
  'Digital Artwork',
  'Music / Audio',
  'Template',
  'Voice-over',
  'Creative Service',
  'Other',
];

const productSubcategories = {
  'Original Painting': ['Watercolor', 'Acrylic', 'Oil painting', 'Madhubani', 'Warli', 'Gond', 'Miniature', 'Portrait', 'Landscape', 'Other'],
  Sculpture: ['Clay', 'Terracotta', 'Metal', 'Wood', 'Stone', 'Mixed media', 'Other'],
  'Handmade Craft': ['Textile', 'Pottery', 'Jewellery', 'Home decor', 'Paper craft', 'Folk craft', 'Other'],
  Photography: ['Fine art print', 'Event photography', 'Portrait', 'Nature', 'Travel', 'Other'],
  'Digital Artwork': ['Digital painting', 'Illustration', 'Printable art', 'AI assisted', 'Template', 'Other'],
  'Music / Audio': ['Song', 'Beat', 'Background score', 'Jingle', 'Other'],
  Template: ['Poster template', 'Social media template', 'Invitation', 'Presentation', 'Other'],
  'Voice-over': ['Hindi voice-over', 'English voice-over', 'Regional language', 'Character voice', 'Other'],
  'Creative Service': ['Custom artwork', 'Workshop', 'Performance booking', 'Design service', 'Other'],
  Other: ['Other'],
};

const productMediumOptions = [
  'Acrylic on canvas',
  'Watercolor on paper',
  'Oil on canvas',
  'Digital file',
  'Terracotta',
  'Clay',
  'Wood',
  'Metal',
  'Textile',
  'Mixed media',
  'Service',
  'Other',
];

const productCityOptions = ['New Delhi', 'Delhi', 'Mumbai', 'Kolkata', 'Bengaluru', 'Chennai', 'Hyderabad', 'Pune', 'Ahmedabad', 'Chandigarh', 'Jaipur', 'Lucknow', 'Ghaziabad', 'Noida', 'Gurugram', 'Faridabad', 'Greater Noida', 'Navi Mumbai', 'Thane', 'Surat', 'Vadodara', 'Indore', 'Bhopal', 'Nagpur', 'Patna', 'Ranchi', 'Bhubaneswar', 'Guwahati', 'Kochi', 'Coimbatore', 'Visakhapatnam', 'Other'];

const productDimensionUnits = [
  { value: 'inches', label: 'Inches' },
  { value: 'feet', label: 'Feet' },
  { value: 'cm', label: 'Centimetres' },
  { value: 'metres', label: 'Metres' },
  { value: 'minutes', label: 'Minutes / duration' },
  { value: 'custom', label: 'Custom text' },
];

const productTypes = [
  { value: 'physical', label: 'Physical artwork / craft' },
  { value: 'digital', label: 'Digital asset' },
  { value: 'service', label: 'Creative service' },
];

const productStatuses = ['draft', 'pending_review', 'live', 'paused', 'published', 'rejected', 'sold_out', 'archived'];
const orderStatuses = ['pending', 'confirmed', 'cancelled', 'fulfilled', 'refunded'];
const paymentStatuses = ['offline_pending', 'pending', 'paid', 'failed', 'refunded'];

const formatPrice = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const normalizeList = (data, key) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const productToForm = (product) => {
  const productCity = product.shipping_from || '';
  return {
  artist_id: product.artist_id || '',
  title: product.title || '',
  description: product.description || '',
  category: product.category || 'Original Painting',
  subcategory: product.subcategory || '',
  product_type: product.product_type || 'physical',
  price_inr: product.price_inr || '',
  discount_percent: product.discount_percent ?? 0,
  inventory_count: product.inventory_count ?? 1,
  image_urls_text: Array.isArray(product.image_urls) ? product.image_urls.join('\n') : '',
  digital_asset_url: product.digital_asset_url || '',
  shipping_from: productCityOptions.includes(productCity) ? productCity : productCity ? 'Other' : '',
  shipping_from_other: productCityOptions.includes(productCity) ? '' : productCity,
  dimensions: product.dimensions || '',
  dimension_value: product.dimensions || '',
  dimension_unit: 'custom',
  medium: product.medium || '',
  status: product.status || 'draft',
  commission_percent: 0,
  admin_note: product.admin_note || '',
  };
};

function FieldNote({ children }) {
  return <p className="mt-1 text-xs font-semibold leading-relaxed text-purple-700">{children}</p>;
}

export default function AdminMarketplace({ section = 'products' }) {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(emptyProduct);
  const [editingId, setEditingId] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [orderPaymentFilter, setOrderPaymentFilter] = useState('');
  const [orderDeletedFilter, setOrderDeletedFilter] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState('');
  const [orderDrafts, setOrderDrafts] = useState({});
  const [updatingOrderId, setUpdatingOrderId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [uploading, setUploading] = useState(false);
  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const body = new FormData(); body.append('file', file);
      const { data } = await adminApi.post('/admin/uploads/portfolio-image', body);
      setForm(current => ({ ...current, image_urls_text: [current.image_urls_text, data.image_url].filter(Boolean).join('\n') }));
    } catch (err) { setError(err.response?.data?.detail || 'Image upload failed.'); }
    finally { setUploading(false); event.target.value = ''; }
  };

  const orderSummary = useMemo(() => {
    const summary = {
      total: orders.length,
      pending: 0,
      paid: 0,
      unpaid: 0,
      payoutDue: 0,
    };
    orders.forEach((order) => {
      if ((order.status || 'pending') === 'pending') summary.pending += 1;
      if (order.payment_status === 'paid') summary.paid += 1;
      if (order.payment_status !== 'paid') summary.unpaid += 1;
      if (order.payment_status === 'paid' && order.payout_status !== 'paid') {
        summary.payoutDue += Number(order.artist_payout_inr || 0);
      }
    });
    return summary;
  }, [orders]);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [productsResponse, ordersResponse] = await Promise.all([
        adminApi.get('/admin/marketplace/products', {
          params: {
            search: productSearch.trim(),
            status: statusFilter,
          },
        }),
        adminApi.get('/admin/marketplace/orders', {
          params: {
            search: orderSearch.trim(),
            status: orderStatusFilter,
            payment_status: orderPaymentFilter,
            deleted: orderDeletedFilter,
          },
        }),
      ]);
      setProducts(normalizeList(productsResponse.data, 'products'));
      setOrders(normalizeList(ordersResponse.data, 'orders'));
      setSelectedOrderIds([]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load marketplace data. Confirm backend marketplace routes are deployed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateForm = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'category') {
        const allowedSubcategories = productSubcategories[value] || [];
        if (!allowedSubcategories.includes(next.subcategory)) {
          next.subcategory = allowedSubcategories[0] || '';
        }
      }
      return next;
    });
  };

  const resetForm = () => {
    setForm(emptyProduct);
    setEditingId('');
    setMessage('');
    setError('');
  };

  const submitProduct = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    const shippingFrom = form.shipping_from === 'Other' ? form.shipping_from_other.trim() : form.shipping_from;
    const payload = {
      ...form,
      shipping_from: shippingFrom,
      dimensions: form.dimension_value
        ? form.dimension_unit === 'custom'
          ? form.dimension_value
          : `${form.dimension_value} ${form.dimension_unit}`
        : form.dimensions,
      price_inr: Number(form.price_inr || 0),
      inventory_count: Number(form.inventory_count || 0),
      commission_percent: 0,
      discount_percent: Number(form.discount_percent || 0),
      image_urls: form.image_urls_text
        .split(/\n|,/)
        .map((url) => url.trim())
        .filter(Boolean),
      source_generation_id: '',
    };
    delete payload.image_urls_text;
    delete payload.dimension_value;
    delete payload.dimension_unit;
    delete payload.shipping_from_other;
    try {
      if (editingId) {
        await adminApi.put(`/admin/marketplace/products/${editingId}`, payload);
        setMessage('Marketplace product updated.');
      } else {
        await adminApi.post('/admin/marketplace/products', payload);
        setMessage('Marketplace product created.');
      }
      await loadAll();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save marketplace product.');
    } finally {
      setSaving(false);
    }
  };

  const editProduct = (product) => {
    setEditingId(product.id);
    setForm(productToForm(product));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const changeProductStatus = async (product, action) => {
    setMessage('');
    setError('');
    try {
      if (action === 'reject') {
        const rejectionReason = window.prompt('Reason for rejecting this product listing?');
        if (!rejectionReason?.trim()) return;
        await adminApi.post(`/admin/marketplace/products/${product.id}/reject`, {
          rejection_reason: rejectionReason.trim(),
        });
      } else {
        await adminApi.post(`/admin/marketplace/products/${product.id}/${action}`);
      }
      const actionLabels = {
        approve: 'Product approved for KalaManch Market.',
        publish: 'Product approved for KalaManch Market.',
        pause: 'Product paused.',
        reject: 'Product rejected with feedback.',
        archive: 'Product archived.',
      };
      setMessage(actionLabels[action] || 'Product updated.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update product status.');
    }
  };

  const updateOrder = async (order, update) => {
    setUpdatingOrderId(order.id);
    setMessage('');
    setError('');
    try {
      await adminApi.patch(`/admin/marketplace/orders/${order.id}`, update);
      setMessage('Order updated.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update order.');
    } finally {
      setUpdatingOrderId('');
    }
  };

  const updateOrderDraft = (orderId, field, value) => {
    setOrderDrafts((current) => ({
      ...current,
      [orderId]: {
        ...(current[orderId] || {}),
        [field]: value,
      },
    }));
  };

  const saveOrderAdminNote = async (order) => {
    const draft = orderDrafts[order.id] || {};
    await updateOrder(order, {
      admin_note: draft.admin_note ?? order.admin_note ?? '',
      delivery_status: draft.delivery_status ?? order.delivery_status ?? 'pending',
    });
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((current) =>
      current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId]
    );
  };

  const selectAllVisibleOrders = () => {
    const visibleIds = orders.map((order) => order.id).filter(Boolean);
    setSelectedOrderIds((current) => (current.length === visibleIds.length ? [] : visibleIds));
  };

  const runBulkOrderAction = async (action, orderIds = selectedOrderIds) => {
    const cleanIds = orderIds.filter(Boolean);
    if (cleanIds.length === 0) {
      setError('Please select at least one marketplace enquiry.');
      return;
    }
    if (action === 'delete') {
      const confirmed = window.confirm(`Delete ${cleanIds.length} selected marketplace enquiry record(s)? You can restore them from Deleted orders.`);
      if (!confirmed) return;
    }
    setUpdatingOrderId('bulk');
    setMessage('');
    setError('');
    try {
      const response = await adminApi.post('/admin/marketplace/orders/bulk', {
        action,
        order_ids: cleanIds,
      });
      setMessage(
        action === 'restore'
          ? `${response.data?.updated_count || cleanIds.length} marketplace enquiry record(s) restored.`
          : `${response.data?.updated_count || cleanIds.length} marketplace enquiry record(s) deleted. Use Deleted orders to undo.`
      );
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update selected marketplace enquiries.');
    } finally {
      setUpdatingOrderId('');
    }
  };

  return (
    <div className="space-y-8">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-sm sm:p-8">
        <p className="text-xs font-black uppercase tracking-[0.45em] text-[#FF6F00]">Marketplace</p>
        <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-black text-slate-950 sm:text-4xl">
              {section === 'orders' ? 'Marketplace orders' : 'Marketplace products'}
            </h1>
            <p className="mt-3 max-w-3xl text-slate-600">
              Only KalaManch products and services appear here. Artist listings are retired. Customers browse publicly and use a separate marketplace account for purchase requests.
            </p>
          </div>
          <button type="button" className="btn-outline" onClick={loadAll}>
            Refresh
          </button>
        </div>
      </section>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 font-semibold text-red-700">{error}</div>}
      {message && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 font-semibold text-emerald-700">{message}</div>}

      {section === 'products' && <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <form className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-sm" onSubmit={submitProduct}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.35em] text-purple-700">Product editor</p>
              <h2 className="mt-2 text-2xl font-black text-slate-950">{editingId ? 'Edit marketplace product' : 'Create product listing'}</h2>
            </div>
            {editingId && (
              <button type="button" className="btn-outline" onClick={resetForm}>
                New product
              </button>
            )}
          </div>

          <p className="mt-6 text-sm font-semibold">Sold and managed by KalaManch.</p>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label>
              <span className="text-sm font-bold text-slate-800">Product title</span>
              <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.title} onChange={(event) => updateForm('title', event.target.value)} required />
              <FieldNote>Example: Ganga Ghat Watercolor, Terracotta Bird Set, Voice-over Pack.</FieldNote>
            </label>
            <label>
              <span className="text-sm font-bold text-slate-800">Price in INR</span>
              <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" type="number" min="1" value={form.price_inr} onChange={(event) => updateForm('price_inr', event.target.value)} required />
              <FieldNote>List price before any discount.</FieldNote>
            </label>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <label>
              <span className="text-sm font-bold text-slate-800">Category</span>
              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.category} onChange={(event) => updateForm('category', event.target.value)}>
                <option value="">Select category</option>
                {productCategories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="text-sm font-bold text-slate-800">Subcategory / medium</span>
              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.subcategory} onChange={(event) => updateForm('subcategory', event.target.value)}>
                <option value="">Select subcategory</option>
                {(productSubcategories[form.category] || []).map((subcategory) => (
                  <option key={subcategory} value={subcategory}>{subcategory}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="text-sm font-bold text-slate-800">Product type</span>
              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.product_type} onChange={(event) => updateForm('product_type', event.target.value)}>
                {productTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <label>
              <span className="text-sm font-bold text-slate-800">Inventory</span>
              <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" type="number" min="0" value={form.inventory_count} onChange={(event) => updateForm('inventory_count', event.target.value)} />
              <FieldNote>Use 1 for original pieces, higher for prints or digital assets.</FieldNote>
            </label>
            <label>
              <span className="text-sm font-bold text-slate-800">Discount %</span>
              <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" type="number" min="0" max="99" value={form.discount_percent} onChange={(event) => updateForm('discount_percent', event.target.value)} />
              <FieldNote>Discount applied to the listed price at checkout.</FieldNote>
            </label>
            <label>
              <span className="text-sm font-bold text-slate-800">Status</span>
              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.status} onChange={(event) => updateForm('status', event.target.value)}>
                {productStatuses.map((status) => (
                  <option key={status} value={status}>{status.replace('_', ' ')}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="mt-4 block">
            <span className="text-sm font-bold text-slate-800">Description</span>
            <textarea className="mt-2 min-h-28 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.description} onChange={(event) => updateForm('description', event.target.value)} placeholder="Describe the artwork, craft, asset, usage rights, shipping note, or service details." />
            <FieldNote>Describe the product or service, what is included, and delivery details.</FieldNote>
          </label>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label>
              <span className="text-sm font-bold text-slate-800">Image URLs</span>
              <textarea className="mt-2 min-h-24 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.image_urls_text} onChange={(event) => updateForm('image_urls_text', event.target.value)} placeholder="One image URL per line" />
              <input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={uploadImage} aria-label="Upload product image" className="mt-3" /><FieldNote>{uploading ? "Uploading image…" : "Upload a product photo or enter image URLs."}</FieldNote>
            </label>
            <label>
              <span className="text-sm font-bold text-slate-800">Digital asset URL</span>
              <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.digital_asset_url} onChange={(event) => updateForm('digital_asset_url', event.target.value)} placeholder="Optional private delivery URL or note" />
              <FieldNote>Use for downloadable art, music, templates, or voice-over deliveries.</FieldNote>
            </label>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <label>
              <span className="text-sm font-bold text-slate-800">Medium</span>
              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.medium} onChange={(event) => updateForm('medium', event.target.value)}>
                <option value="">Select medium</option>
                {productMediumOptions.map((medium) => <option key={medium} value={medium}>{medium}</option>)}
              </select>
            </label>
            <label>
              <span className="text-sm font-bold text-slate-800">Ships from city</span>
              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.shipping_from} onChange={(event) => updateForm('shipping_from', event.target.value)}>
                <option value="">Select city</option>
                {productCityOptions.map((city) => <option key={city} value={city}>{city}</option>)}
              </select>
              {form.shipping_from === 'Other' && (
                <input className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.shipping_from_other} onChange={(event) => updateForm('shipping_from_other', event.target.value)} placeholder="Type city name" required />
              )}
            </label>
            <div>
              <span className="text-sm font-bold text-slate-800">Dimensions / duration</span>
              <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_10rem]">
                <input className="w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.dimension_value} onChange={(event) => updateForm('dimension_value', event.target.value)} placeholder="Example: 24 x 36" />
                <select className="w-full rounded-2xl border border-slate-200 px-3 py-3" value={form.dimension_unit} onChange={(event) => updateForm('dimension_unit', event.target.value)}>
                  {productDimensionUnits.map((unit) => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <label className="mt-4 block">
            <span className="text-sm font-bold text-slate-800">Admin note</span>
            <textarea className="mt-2 min-h-20 w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.admin_note} onChange={(event) => updateForm('admin_note', event.target.value)} placeholder="Internal note, payout note, authenticity note" />
          </label>

          <button type="submit" className="btn-primary mt-6" disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Save product' : 'Create product'}
          </button>
        </form>

        <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-sm">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-purple-700">Marketplace products</p>
          <h2 className="mt-2 text-2xl font-black text-slate-950">Review listings</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <input className="rounded-2xl border border-slate-200 px-4 py-3" value={productSearch} onChange={(event) => setProductSearch(event.target.value)} placeholder="Search products" />
            <select className="rounded-2xl border border-slate-200 px-4 py-3" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">All statuses</option>
              {productStatuses.map((status) => (
                <option key={status} value={status}>{status.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <button type="button" className="btn-outline mt-3" onClick={loadAll}>Apply filters</button>

          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="rounded-2xl bg-slate-50 p-4 font-semibold text-slate-500">Loading marketplace...</div>
            ) : products.length === 0 ? (
              <div className="rounded-2xl bg-slate-50 p-4 font-semibold text-slate-500">No marketplace products yet.</div>
            ) : (
              products.map((product) => (
                <article key={product.id} className="rounded-3xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex gap-3">
                    {product.image_urls?.[0] ? (
                      <img src={product.image_urls[0]} alt="" className="h-20 w-20 rounded-2xl object-cover" />
                    ) : (
                      <div className="h-20 w-20 rounded-2xl bg-white" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-600">{product.status}</p>
                      <h3 className="line-clamp-2 text-lg font-black text-slate-950">{product.title}</h3>
                      <p className="text-sm font-semibold text-purple-700">KalaManch · {formatPrice(product.price_inr)}</p>
                      <p className="text-xs text-slate-500">{product.category} {product.subcategory ? `· ${product.subcategory}` : ''}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button type="button" className="btn-outline !px-4 !py-2" onClick={() => editProduct(product)}>Edit</button>
                    {!['live', 'published'].includes(product.status) && (
                      <button type="button" className="btn-primary !px-4 !py-2" onClick={() => changeProductStatus(product, 'approve')}>Approve</button>
                    )}
                    {product.status === 'pending_review' && (
                      <button type="button" className="rounded-full border border-red-200 px-4 py-2 text-sm font-black text-red-600" onClick={() => changeProductStatus(product, 'reject')}>Reject</button>
                    )}
                    {['live', 'published'].includes(product.status) && (
                      <button type="button" className="btn-outline !px-4 !py-2" onClick={() => changeProductStatus(product, 'pause')}>Pause</button>
                    )}
                    {product.status !== 'archived' && (
                      <button type="button" className="rounded-full border border-red-200 px-4 py-2 text-sm font-black text-red-600" onClick={() => changeProductStatus(product, 'archive')}>Archive</button>
                    )}
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      </section>}

      {section === 'orders' && <section className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.35em] text-emerald-700">Marketplace enquiries</p>
            <h2 className="mt-2 text-2xl font-black text-slate-950">Admin coordination desk</h2>
            <p className="mt-2 max-w-3xl text-slate-600">
              Customer contact and delivery details are available only to administrators and the customer who placed the order.
            </p>
          </div>
          <button type="button" className="btn-outline whitespace-nowrap" onClick={loadAll}>
            Refresh orders
          </button>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Total</p>
            <p className="mt-1 text-2xl font-black text-slate-950">{orderSummary.total}</p>
          </div>
          <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-orange-600">Pending</p>
            <p className="mt-1 text-2xl font-black text-orange-700">{orderSummary.pending}</p>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-emerald-700">Paid</p>
            <p className="mt-1 text-2xl font-black text-emerald-800">{orderSummary.paid}</p>
          </div>
          <div className="rounded-2xl border border-red-100 bg-red-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-red-600">Unpaid</p>
            <p className="mt-1 text-2xl font-black text-red-700">{orderSummary.unpaid}</p>
          </div>
          <div className="rounded-2xl border border-purple-100 bg-purple-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-purple-700">Order value</p>
            <p className="mt-1 text-xl font-black text-purple-900">{formatPrice(orders.reduce((total, order) => total + Number(order.total_inr || 0), 0))}</p>
          </div>
        </div>

        <div className="mt-6 grid gap-3 lg:grid-cols-[1fr_13rem_13rem_12rem_auto]">
          <input
            className="rounded-2xl border border-slate-200 px-4 py-3"
            value={orderSearch}
            onChange={(event) => setOrderSearch(event.target.value)}
            placeholder="Search buyer, seller, product, phone, address, note"
          />
          <select
            className="rounded-2xl border border-slate-200 px-4 py-3"
            value={orderStatusFilter}
            onChange={(event) => setOrderStatusFilter(event.target.value)}
          >
            <option value="">All order statuses</option>
            {orderStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <select
            className="rounded-2xl border border-slate-200 px-4 py-3"
            value={orderPaymentFilter}
            onChange={(event) => setOrderPaymentFilter(event.target.value)}
          >
            <option value="">All payment statuses</option>
            {paymentStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <select
            className="rounded-2xl border border-slate-200 px-4 py-3"
            value={orderDeletedFilter}
            onChange={(event) => setOrderDeletedFilter(event.target.value)}
          >
            <option value="">Active orders</option>
            <option value="deleted">Deleted orders</option>
          </select>
          <button type="button" className="btn-primary whitespace-nowrap" onClick={loadAll}>
            Apply filters
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-100 bg-slate-50 p-3">
          <button type="button" className="btn-outline !px-4 !py-2 text-sm" onClick={selectAllVisibleOrders} disabled={orders.length === 0}>
            {selectedOrderIds.length === orders.length && orders.length > 0 ? 'Clear selection' : 'Select all visible'}
          </button>
          <span className="text-sm font-bold text-slate-600">{selectedOrderIds.length} selected</span>
          {orderDeletedFilter === 'deleted' ? (
            <button type="button" className="btn-primary !px-4 !py-2 text-sm" disabled={updatingOrderId === 'bulk' || selectedOrderIds.length === 0} onClick={() => runBulkOrderAction('restore')}>
              Undo delete / restore selected
            </button>
          ) : (
            <>
              <button type="button" className="rounded-full border border-red-200 px-4 py-2 text-sm font-black text-red-600 disabled:opacity-50" disabled={updatingOrderId === 'bulk' || selectedOrderIds.length === 0} onClick={() => runBulkOrderAction('delete')}>
                Delete selected
              </button>
              <button type="button" className="rounded-full border border-red-200 px-4 py-2 text-sm font-black text-red-600 disabled:opacity-50" disabled={updatingOrderId === 'bulk' || orders.length === 0} onClick={() => runBulkOrderAction('delete', orders.map((order) => order.id))}>
                Delete all visible
              </button>
            </>
          )}
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="rounded-2xl bg-slate-50 p-5 font-semibold text-slate-500">Loading marketplace enquiries...</div>
          ) : orders.length === 0 ? (
            <div className="rounded-2xl bg-slate-50 p-5 font-semibold text-slate-500">No marketplace enquiries match these filters.</div>
          ) : (
            orders.map((order) => {
              const isExpanded = expandedOrderId === order.id;
              const isSelected = selectedOrderIds.includes(order.id);
              const draft = orderDrafts[order.id] || {};
              const noteValue = draft.admin_note ?? order.admin_note ?? '';
              const payoutValue = draft.delivery_status ?? order.delivery_status ?? 'pending';
              return (
                <article key={order.id} className={`rounded-3xl border p-5 shadow-sm ${order.is_deleted ? 'border-red-100 bg-red-50/60' : 'border-emerald-100 bg-gradient-to-br from-white to-emerald-50/40'}`}>
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-700">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOrderSelection(order.id)}
                          />
                          Select
                        </label>
                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black uppercase text-emerald-800">{order.status || 'pending'}</span>
                        <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-black text-orange-700">{order.payment_status || 'offline_pending'}</span>
                        {order.is_deleted && <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-black text-red-700">Deleted</span>}
                      </div>
                      <h3 className="mt-3 text-xl font-black text-slate-950">{order.product_title || 'Marketplace enquiry'}</h3>
                      <p className="mt-1 text-sm font-semibold text-slate-600">
                        Qty {order.quantity || 1} | {formatPrice(order.total_inr)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className="btn-outline !px-4 !py-2 text-sm" onClick={() => setExpandedOrderId(isExpanded ? '' : order.id)}>
                        {isExpanded ? 'Close details' : 'Manage enquiry'}
                      </button>
                      <button type="button" className="btn-primary !px-4 !py-2 text-sm" disabled={updatingOrderId === order.id || order.is_deleted} onClick={() => updateOrder(order, { status: 'confirmed' })}>
                        Confirm
                      </button>
                      <button type="button" className="rounded-full border border-red-200 px-4 py-2 text-sm font-black text-red-600" disabled={updatingOrderId === order.id || order.is_deleted} onClick={() => {
                        if (window.confirm('Cancel this marketplace enquiry?')) {
                          updateOrder(order, { status: 'cancelled', admin_note: order.admin_note || 'Cancelled by admin.' });
                        }
                      }}>
                        Cancel
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 lg:grid-cols-3">
                    <div className="rounded-2xl border border-slate-100 bg-white p-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Buyer</p>
                      <p className="mt-2 font-black text-slate-950">{order.buyer_name || 'Customer'}</p>
                      <p className="text-sm font-semibold text-slate-600">{order.buyer_email || 'No email'}</p>
                      <p className="text-sm font-semibold text-slate-600">{order.buyer_phone || 'No phone'}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-100 bg-white p-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Seller</p>
                      <p className="mt-2 font-black text-slate-950">KalaManch</p>
                      <p className="text-sm font-semibold text-slate-600">Platform store</p>
                      <p className="text-xs font-semibold text-slate-500">Managed by administrators.</p>
                    </div>
                    <div className="rounded-2xl border border-slate-100 bg-white p-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Next admin step</p>
                      <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">
                        Verify inventory, arrange payment with the customer, then update payment and delivery status. Mark refunded only after the refund is completed.
                      </p>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 rounded-3xl border border-white bg-white p-4 shadow-sm">
                      <div className="grid gap-4 lg:grid-cols-2">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6F00]">Buyer request details</p>
                          <p className="mt-3 text-sm font-semibold leading-6 text-slate-700">
                            <strong>Address:</strong> {order.shipping_address || 'Not provided'}
                          </p>
                          <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">
                            <strong>Message:</strong> {order.note || 'No message'}
                          </p>
                          <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">
                            <strong>Order ID:</strong> {order.id}
                          </p>
                          <div className="mt-4 flex flex-wrap gap-2">
                            {order.buyer_email && <a className="btn-outline !px-4 !py-2 text-sm" href={`mailto:${order.buyer_email}`}>Email buyer</a>}
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <label>
                              <span className="text-sm font-bold text-slate-800">Order status</span>
                              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={order.status || 'pending'} onChange={(event) => updateOrder(order, { status: event.target.value })}>
                                {orderStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                              </select>
                            </label>
                            <label>
                              <span className="text-sm font-bold text-slate-800">Payment status</span>
                              <select className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3" value={order.payment_status || 'offline_pending'} onChange={(event) => updateOrder(order, { payment_status: event.target.value })}>
                                {paymentStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                              </select>
                            </label>
                          </div>
                          <label className="block">
                            <span className="text-sm font-bold text-slate-800">Delivery status</span>
                            <input
                              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3"
                              value={payoutValue}
                              onChange={(event) => updateOrderDraft(order.id, 'delivery_status', event.target.value)}
                              placeholder="pending, preparing, dispatched, delivered, returned"
                            />
                          </label>
                          <label className="block">
                            <span className="text-sm font-bold text-slate-800">Admin coordination note</span>
                            <textarea
                              className="mt-2 min-h-28 w-full rounded-2xl border border-slate-200 px-4 py-3"
                              value={noteValue}
                              onChange={(event) => updateOrderDraft(order.id, 'admin_note', event.target.value)}
                              placeholder="Internal note: buyer called, seller confirmed, payment link sent, delivery follow-up..."
                            />
                          </label>
                          <div className="flex flex-wrap gap-2">
                            <button type="button" className="btn-primary !px-4 !py-2 text-sm" disabled={updatingOrderId === order.id} onClick={() => saveOrderAdminNote(order)}>
                              Save admin note
                            </button>
                            <button type="button" className="btn-outline !px-4 !py-2 text-sm" disabled={updatingOrderId === order.id} onClick={() => updateOrder(order, { payment_status: 'paid', status: 'confirmed' })}>
                              Mark paid + confirmed
                            </button>
                            <button type="button" className="btn-outline !px-4 !py-2 text-sm" disabled={updatingOrderId === order.id} onClick={() => updateOrder(order, { status: 'fulfilled' })}>
                              Mark fulfilled
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </article>
              );
            })
          )}
        </div>
      </section>}
    </div>
  );
}
